/**
 * Radiators header tools on phones (2026-10-26, mobile header compaction). The header is one row on a phone (menu,
 * search, logo, account, basket): the search field and the route chips of sections/rad-header-tools.liquid are folded
 * away and open as an overlay under the header when the header's search button is tapped. The overlay is fixed, so
 * opening it never moves the page. On a phone the button opens this panel instead of Horizon's search dialog, so every
 * search goes through the theme query normaliser (assets/rad-search.js). Desktop is unchanged.
 *
 * Keyboard: the button reports aria-expanded and aria-controls; opening moves focus to the search field; Escape or the
 * button again closes it and returns focus to the button; a tap outside closes it.
 *
 * Astra TL-04: the listeners are installed once and look up the tools section that is in the document at the time of
 * each event, so a re-rendered or replaced section (theme editor, section rendering) keeps working without re-binding.
 */
const PHONE = window.matchMedia('(max-width: 749px)');
const BUTTONS = 'header-component .search-action button';

let lastButton = null;
let raf = 0;

/** The connected tools section and its parts, or null. */
function current() {
  const tools = document.querySelector('[data-rad-header-tools]');
  const panel = tools?.querySelector('[data-rad-tools-panel]');
  if (!(tools instanceof HTMLElement) || !(panel instanceof HTMLElement)) return null;
  return { tools, panel, input: tools.querySelector('input[type="search"]') };
}

const buttons = () => [...document.querySelectorAll(BUTTONS)];
const isOpen = (c) => Boolean(c && c.tools.hasAttribute('data-open'));

function place() {
  const c = current();
  if (!c) return;
  const header = document.querySelector('header-component');
  const bottom = header ? header.getBoundingClientRect().bottom : 0;
  c.tools.style.setProperty('--rad-tools-top', `${Math.max(0, Math.round(bottom))}px`);
}

function onScroll() {
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(place);
}

function sync() {
  const c = current();
  if (c && !buttons().length) c.tools.setAttribute('data-static', ''); // no header search button: show it in the page
  for (const b of buttons()) {
    if (PHONE.matches && c) {
      b.setAttribute('aria-controls', c.panel.id);
      b.setAttribute('aria-expanded', String(isOpen(c)));
      b.removeAttribute('aria-haspopup');
    } else {
      b.removeAttribute('aria-controls');
      b.removeAttribute('aria-expanded');
      b.setAttribute('aria-haspopup', 'dialog');
    }
  }
}

function open(c, button) {
  lastButton = button;
  place();
  c.tools.setAttribute('data-open', '');
  window.addEventListener('scroll', onScroll, { passive: true });
  sync();
  if (c.input instanceof HTMLInputElement) c.input.focus({ preventScroll: true });
}

function close(returnFocus) {
  const c = current();
  window.removeEventListener('scroll', onScroll);
  if (c) c.tools.removeAttribute('data-open');
  sync();
  if (returnFocus && lastButton instanceof HTMLElement && lastButton.isConnected) lastButton.focus();
}

// Capture on window: runs before Horizon's delegated on:click handler on document, which would open its dialog.
// Only on a phone and only when the tools section exists; otherwise Horizon's dialog opens as usual.
window.addEventListener('click', (e) => {
  if (!PHONE.matches) return;
  const c = current();
  if (!c) return;
  const target = e.target instanceof Element ? e.target : null;
  const button = target ? target.closest(BUTTONS) : null;
  if (button) {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen(c)) close(false);
    else open(c, button);
    return;
  }
  if (isOpen(c) && target && !c.panel.contains(target)) close(false);
}, true);

document.addEventListener('keydown', (e) => {
  const c = current();
  if (e.key !== 'Escape' || !isOpen(c)) return;
  const active = document.activeElement;
  if (c.panel.contains(active) || buttons().includes(/** @type {any} */ (active))) close(true);
});

PHONE.addEventListener('change', () => close(false));
document.addEventListener('shopify:section:load', sync);
document.addEventListener('shopify:section:unload', () => requestAnimationFrame(() => close(false)));

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
else sync();
